
import Vue, { PropType } from 'vue'
import {
    createCallbotCommandKey,
    getCallbotActivations,
    getCallbotConfiguration,
    getCallbotRunStatus,
    prepareCallbotImportRun,
    saveCallbotDraft,
    prepareCallbotRun,
    recalculateCallbotRun,
    startCallbotRun,
    uploadCallbotAudience,
    attachCallbotMaterial,
    getCallbotMaterialAudio,
    getCallbotMaterials,
    getCallbotTelephonyQuote,
} from '@/api/callbot'
import { CallbotAudienceImportResult } from '@/api/callbot'
import {
    CallbotActivation,
    CallbotConfiguration,
    CallbotNotificationChannel,
    CallbotRun,
    CallbotMaterial,
    CallbotTelephonyQuote,
} from '@/types/callbot'
import TelephonyTopupDialog from './TelephonyTopupDialog.vue'
import ZaIcon from './ZaIcon.vue'
import { formatRub } from './telephony'
import { formatMaterialDuration, formatMaterialCount, RETRY_INTERVALS, REDIAL_OPTIONS, redialLabel } from './audioLibrary'

type Slot = 'morning' | 'day' | 'evening'
type ScheduleOption = Slot | 'custom'
type AudienceType = 'lost' | 'hot' | 'import'

const EMPTY_CONFIGURATION: CallbotConfiguration = {
    administratorPhone: null,
    notificationChannels: [],
    defaultNotificationConnectorId: null,
    timezone: 'Europe/Moscow',
}

const localDateTime = () => {
    const date = new Date(Date.now() + 15 * 60_000)
    date.setMinutes(Math.ceil(date.getMinutes() / 5) * 5, 0, 0)
    const offset = date.getTimezoneOffset() * 60_000
    return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

const commandKeys = () => ({
    prepare: createCallbotCommandKey(),
    recalculate: createCallbotCommandKey(),
    audio: createCallbotCommandKey(),
    start: createCallbotCommandKey(),
})

const createEmptyForm = () => ({
    templateKey: null as string | null,
    audienceType: null as AudienceType | null,
    audienceFile: null as File | null,
    lostFrom: 40,
    lostTo: 540,
    materialId: null as string | null,
    transferPhone: '',
    notificationConnectorId: '',
    timezone: 'Europe/Moscow',
    scheduleOption: 'morning' as ScheduleOption,
    customFrom: '10:00',
    customTo: '14:00',
    startAt: localDateTime(),
    maxAttempts: 1,
    retryIntervalMinutes: 10,
    moduleAEnabled: true,
    moduleAReminderMinutes: 20,
})

export default Vue.extend({
    name: 'CreateCallbotDialog',
    components: { TelephonyTopupDialog, ZaIcon },
    props: {
        value: { type: Boolean, default: false },
        projectId: { type: String, required: true },
        // Сохранённый черновик, который надо продолжить редактировать.
        draft: { type: Object as PropType<CallbotRun | null>, default: null },
        // Ролик, выбранный в карточке «Создать рассылку» во вкладке «Аудио».
        initialMaterial: { type: Object as PropType<CallbotMaterial | null>, default: null },
    },
    data() {
        return {
            step: 1,
            stepTitles: ['Аудитория', 'Сообщение', 'Связь и уведомления', 'Расписание', 'Проверка'],
            activations: [] as CallbotActivation[],
            configuration: { ...EMPTY_CONFIGURATION } as CallbotConfiguration,
            loadingInitial: false,
            busy: false,
            reviewLoading: false,
            error: '',
            launchStatus: '',
            reviewStatus: '',
            quote: null as CallbotTelephonyQuote | null,
            quoteError: '',
            quoteRequestId: 0,
            topupOpen: false,
            technicalRun: null as CallbotRun | null,
            importSummary: null as CallbotAudienceImportResult | null,
            savingDraft: false,
            draftSaved: false,
            launched: false,
            paymentPending: false,
            paymentTimer: null as number | null,
            form: createEmptyForm(),
            keys: commandKeys(),
            materials: [] as CallbotMaterial[],
            materialsLoading: false,
            playingId: '',
            previewPlayer: null as HTMLAudioElement | null,
            previewUrls: {} as Record<string, string>,
            audienceDragActive: false,
            reviewGeneration: 0,
            slotOptions: [
                { value: 'morning' as Slot, title: 'Утро', time: '09:00–12:00' },
                { value: 'day' as Slot, title: 'День', time: '12:00–17:00' },
                { value: 'evening' as Slot, title: 'Вечер', time: '17:00–20:00' },
            ],
            retryIntervals: RETRY_INTERVALS,
            redialOptions: REDIAL_OPTIONS,
        }
    },
    computed: {
        availableActivations(): CallbotActivation[] {
            return this.activations.filter(
                activation => activation.available && activation.sourceActivationId != null
            )
        },
        lostActivation(): CallbotActivation | null {
            return this.availableActivations.find(item => item.audienceType === 'lost') || null
        },
        hotActivation(): CallbotActivation | null {
            return this.availableActivations.find(item => item.audienceType === 'hot') || null
        },
        selectedActivation(): CallbotActivation | null {
            if (this.form.audienceType === 'lost') return this.lostActivation
            if (this.form.audienceType === 'hot') return this.hotActivation
            return null
        },
        selectedActivationName(): string {
            if (this.form.audienceType === 'lost') {
                return `Потерянные клиенты от ${this.form.lostFrom} до ${this.form.lostTo} дней`
            }
            if (this.form.audienceType === 'hot') return 'Горячие клиенты'
            return '—'
        },
        lostRangeError(): string {
            const from = Number(this.form.lostFrom)
            const to = Number(this.form.lostTo)
            if (!Number.isInteger(from) || from < 40 || from > 998) {
                return 'Начало диапазона должно быть от 40 до 998 дней.'
            }
            if (!Number.isInteger(to) || to < 40 || to > 999) {
                return 'Окончание диапазона должно быть от 40 до 999 дней.'
            }
            if (from >= to) return 'Начало диапазона должно быть меньше окончания.'
            return ''
        },
        selectedChannel(): CallbotNotificationChannel | null {
            return (
                this.configuration.notificationChannels.find(
                    channel => channel.connectorId === this.form.notificationConnectorId
                ) || null
            )
        },
        selectedChannelLabel(): string {
            return this.selectedChannel?.label || '—'
        },
        campaignName(): string {
            const date = new Intl.DateTimeFormat('ru-RU').format(new Date())
            return `${this.selectedActivationName} · ${date}`
        },
        reviewEligibleCount(): number {
            return this.technicalRun?.calculation.eligible || 0
        },
        scheduleLabel(): string {
            if (this.form.scheduleOption === 'custom') {
                return `${this.form.customFrom}–${this.form.customTo}`
            }
            return this.slotOptions.find(slot => slot.value === this.form.scheduleOption)?.time || '—'
        },
        formattedStartAt(): string {
            const date = new Date(this.form.startAt)
            if (Number.isNaN(date.getTime())) return '—'
            return new Intl.DateTimeFormat('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(date)
        },
        moduleALabel(): string {
            return this.form.moduleAEnabled ? `Через ${this.form.moduleAReminderMinutes} минут` : 'Выключено'
        },
        validTransferPhone(): boolean {
            return this.form.transferPhone.replace(/\D/g, '').length >= 10
        },
        validCustomWindow(): boolean {
            return this.form.scheduleOption !== 'custom' || this.form.customFrom < this.form.customTo
        },
        validSchedule(): boolean {
            return (
                Boolean(this.form.scheduleOption) &&
                this.validCustomWindow &&
                !Number.isNaN(new Date(this.form.startAt).getTime())
            )
        },
        reviewDisplayReady(): boolean {
            return this.reviewReady
        },
        reviewReady(): boolean {
            return Boolean(
                this.technicalRun &&
                    this.technicalRun.calculation.status === 'ready' &&
                    this.technicalRun.calculation.eligible > 0
            )
        },
        approvedMaterials(): CallbotMaterial[] {
            return this.materials.filter(material => material.status === 'approved')
        },
        selectedMaterial(): CallbotMaterial | null {
            return this.approvedMaterials.find(material => material.id === this.form.materialId) || null
        },
        canSaveDraft(): boolean {
            return Boolean(this.technicalRun)
        },
        // Пересчёт аудитории идёт в фоне со второго шага — показываем ход и
        // завершение, пока пользователь заполняет остальные настройки.
        audienceProgress(): { state: 'running' | 'done'; text: string } | null {
            if (this.step === 1 || this.step === this.stepTitles.length) return null
            if (this.reviewLoading) return { state: 'running', text: 'Пересчитываем аудиторию…' }
            if (this.reviewReady) {
                return {
                    state: 'done',
                    text: `Аудитория пересчитана: ${this.formatNumber(this.reviewEligibleCount)} клиентов`,
                }
            }
            return null
        },
        // Денег не хватает на аренду — вместо запуска открываем пополнение.
        needsTopup(): boolean {
            return Boolean(this.quote && !this.quote.enough)
        },
        nextLabel(): string {
            if (this.step !== this.stepTitles.length) return 'Далее →'
            return this.needsTopup ? 'Пополнить баланс' : 'Запустить аудиорассылку'
        },
        nextDisabled(): boolean {
            if (this.busy) return true
            if (this.reviewLoading && (this.step === 1 || this.step === this.stepTitles.length)) {
                return true
            }
            if (this.step === 1) {
                if (this.form.audienceType === 'import') return !this.form.audienceFile
                return (
                    !this.selectedActivation ||
                    (this.form.audienceType === 'lost' && Boolean(this.lostRangeError))
                )
            }
            if (this.step === 2) return !this.selectedMaterial
            if (this.step === 3) return !this.validTransferPhone || !this.selectedChannel
            if (this.step === 4) return !this.validSchedule
            return !this.reviewReady
        },
    },
    watch: {
        reviewDisplayReady(ready: boolean) {
            if (ready) this.loadQuote()
        },
        topupOpen(open: boolean) {
            // Пополнение могло пройти в другой вкладке — пересчитываем.
            if (!open && this.reviewDisplayReady) this.loadQuote()
        },
        value(open: boolean) {
            if (open) {
                this.reset()
                this.loadInitialData()
            } else {
                this.cancelReview()
                this.stopPreview()
            }
        },
    },
    mounted() {
        window.addEventListener('pagehide', this.onPageHide)
        document.addEventListener('visibilitychange', this.onVisibilityChange)
    },
    beforeDestroy() {
        window.removeEventListener('pagehide', this.onPageHide)
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
        // Уход на другую страницу приложения — тоже сохраняем.
        if (this.value) this.autosaveDraft()
        this.stopPaymentWatch()
        this.cancelReview()
        this.stopPreview()
        Object.values(this.previewUrls).forEach(url => URL.revokeObjectURL(url))
    },
    methods: {
        materialCount: formatMaterialCount,
        redialText: redialLabel,
        reset() {
            this.cancelReview()
            this.stopPreview()
            this.quote = null
            this.quoteError = ''
            this.draftSaved = false
            this.launched = false
            this.paymentPending = false
            this.stopPaymentWatch()
            this.quoteRequestId += 1
            this.step = 1
            this.error = ''
            this.launchStatus = ''
            this.reviewStatus = ''
            this.technicalRun = null
            this.importSummary = null
            this.form = createEmptyForm()
            this.hydrateFromDraft()
            this.keys = commandKeys()
            if (this.initialMaterial) this.form.materialId = this.initialMaterial.id
            this.configuration = { ...EMPTY_CONFIGURATION }
        },
        async loadInitialData() {
            this.loadingInitial = true
            this.materialsLoading = true
            const [activations, configuration, materials] = await Promise.all([
                getCallbotActivations(this.projectId),
                getCallbotConfiguration(this.projectId),
                getCallbotMaterials(this.projectId),
            ])
            this.materialsLoading = false
            this.materials = materials.data || []
            this.loadingInitial = false
            if (activations.error) {
                this.error = activations.error.message || 'Не удалось загрузить активации'
            } else {
                this.activations = activations.data || []
            }
            if (configuration.data) {
                this.configuration = configuration.data
                this.form.timezone = configuration.data.timezone || 'Europe/Moscow'
                this.form.transferPhone = configuration.data.administratorPhone || ''
                this.form.notificationConnectorId =
                    configuration.data.defaultNotificationConnectorId ||
                    configuration.data.notificationChannels[0]?.connectorId ||
                    ''
            } else if (configuration.error) {
                this.error = configuration.error.message || 'Не удалось загрузить настройки проекта'
            }
        },
        money: formatRub,
        numbersWord(count: number) {
            const mod10 = count % 10
            const mod100 = count % 100
            if (mod10 === 1 && mod100 !== 11) return 'номер'
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'номера'
            return 'номеров'
        },
        async loadQuote() {
            const requestId = ++this.quoteRequestId
            this.quoteError = ''
            const { data, error } = await getCallbotTelephonyQuote(
                this.projectId,
                this.reviewEligibleCount,
                this.form.timezone
            )
            if (requestId !== this.quoteRequestId) return
            this.quote = data
            if (data?.enough) {
                this.paymentPending = false
                this.stopPaymentWatch()
            }
            if (error) this.quoteError = 'Не удалось рассчитать стоимость номеров. Её покажем при запуске.'
        },
        next() {
            this.error = ''
            if (this.step === this.stepTitles.length) {
                if (this.needsTopup) {
                    this.topupOpen = true
                    return
                }
                this.launch()
                return
            }
            const from = this.step
            this.step += 1
            // Черновик на сервере появляется сразу после выбора аудитории:
            // тогда незапущенную рассылку есть куда сохранить при уходе со
            // страницы, а расчёт аудитории идёт, пока заполняются остальные шаги.
            if (from === 1 || (this.step === this.stepTitles.length && !this.reviewLoading)) {
                this.prepareReview()
            }
        },
        back() {
            this.error = ''
            this.step = Math.max(1, this.step - 1)
            // Расчёт аудитории прерываем, только когда возвращаются к аудитории.
            if (this.step === 1 && this.reviewLoading) this.cancelReview()
        },
        editStep(step: number) {
            if (step === 1 && this.reviewLoading) this.cancelReview()
            this.step = step
            this.error = ''
        },
        selectScheduleOption(option: ScheduleOption) {
            this.form.scheduleOption = option
        },
        invalidateAudience() {
            this.technicalRun = null
            this.keys.prepare = createCallbotCommandKey()
            this.keys.recalculate = createCallbotCommandKey()
        },
        // Сохранять можно только то, что уже стало черновиком на сервере:
        // визард создаёт его на шаге проверки.
        hydrateFromDraft() {
            const draft = this.draft
            if (!draft) return
            // Черновик уже существует на сервере — продолжаем именно его,
            // а не создаём второй на шаге проверки.
            this.technicalRun = draft
            const state = (draft.draftState || {}) as Record<string, unknown>
            const pick = <T>(key: string, fallback: T): T =>
                state[key] === undefined ? fallback : (state[key] as T)
            this.form = {
                ...this.form,
                templateKey: pick('templateKey', this.form.templateKey),
                audienceType: pick('audienceType', this.form.audienceType),
                lostFrom: pick('lostFrom', this.form.lostFrom),
                lostTo: pick('lostTo', this.form.lostTo),
                transferPhone: pick('transferPhone', this.form.transferPhone),
                materialId: pick('materialId', this.form.materialId),
                notificationConnectorId: pick(
                    'notificationConnectorId',
                    this.form.notificationConnectorId
                ),
                scheduleOption: pick('scheduleOption', this.form.scheduleOption),
                maxAttempts: pick('maxAttempts', this.form.maxAttempts),
                retryIntervalMinutes: pick(
                    'retryIntervalMinutes',
                    this.form.retryIntervalMinutes
                ),
                moduleAEnabled: pick('moduleAEnabled', this.form.moduleAEnabled),
                moduleAReminderMinutes: pick(
                    'moduleAReminderMinutes',
                    this.form.moduleAReminderMinutes
                ),
            }
        },
        draftPayload() {
            return {
                projectId: this.projectId,
                runId: this.technicalRun!.runId,
                name: this.campaignName,
                draftState: {
                    audienceType: this.form.audienceType,
                    lostFrom: this.form.lostFrom,
                    lostTo: this.form.lostTo,
                    templateKey: this.form.templateKey,
                    transferPhone: this.form.transferPhone,
                    materialId: this.form.materialId,
                    notificationConnectorId: this.form.notificationConnectorId,
                    scheduleOption: this.form.scheduleOption,
                    maxAttempts: this.form.maxAttempts,
                    retryIntervalMinutes: this.form.retryIntervalMinutes,
                    moduleAEnabled: this.form.moduleAEnabled,
                    moduleAReminderMinutes: this.form.moduleAReminderMinutes,
                },
            }
        },
        async saveDraft() {
            if (!this.technicalRun || this.savingDraft) return
            this.savingDraft = true
            const result = await saveCallbotDraft(this.draftPayload())
            this.savingDraft = false
            if (result.error) {
                this.error = 'Не удалось сохранить черновик'
                return
            }
            this.draftSaved = true
            this.$emit('created', result.data)
            this.close()
        },
        // Незапущенная рассылка не теряется: при закрытии окна, уходе со
        // страницы или закрытии вкладки её состояние уходит в черновик.
        async autosaveDraft(keepalive = false) {
            if (!this.technicalRun || this.launched || this.draftSaved) return
            const result = await saveCallbotDraft(
                this.draftPayload(),
                createCallbotCommandKey(),
                { keepalive }
            )
            if (!result.error) this.$emit('draft-saved', result.data)
        },
        onPageHide() {
            if (this.value) this.autosaveDraft(true)
        },
        onVisibilityChange() {
            // Вернулись из вкладки с оплатой — пересчитываем стоимость.
            if (document.visibilityState === 'visible' && this.paymentPending) this.loadQuote()
        },
        onPaymentOpened() {
            this.paymentPending = true
            this.stopPaymentWatch()
            this.paymentTimer = window.setInterval(() => this.loadQuote(), 5000)
        },
        stopPaymentWatch() {
            if (this.paymentTimer !== null) window.clearInterval(this.paymentTimer)
            this.paymentTimer = null
        },
        selectAudienceSource() {
            // Триггер только открывает панель загрузки — сам пикер файлов
            // появляется в DOM вместе с ней, поэтому клик по нему ждёт
            // перерисовки (см. data-select-import-source в макете).
            this.form.audienceType = 'import'
            this.invalidateAudience()
            this.$nextTick(() => this.pickAudienceFile())
        },
        pickAudienceFile() {
            const input = this.$refs.audienceFile as HTMLInputElement | undefined
            if (input) input.click()
        },
        onAudienceFileInput(event: Event) {
            const input = event.target as HTMLInputElement
            const file = input.files && input.files[0]
            input.value = ''
            this.handleAudienceFile(file || null)
        },
        onAudienceFileDrop(event: DragEvent) {
            this.audienceDragActive = false
            this.handleAudienceFile(event.dataTransfer?.files?.[0] || null)
        },
        handleAudienceFile(file: File | null) {
            if (!file) return
            const extension = (file.name.split('.').pop() || '').toLowerCase()
            if (!['csv', 'txt', 'xls', 'xlsx'].includes(extension)) {
                this.error = 'Поддерживаются CSV, XLSX, XLS и TXT'
                return
            }
            if (file.size > 5 * 1024 * 1024) {
                this.error = 'Файл больше 5 МБ'
                return
            }
            this.error = ''
            // Никаких сетевых запросов на этом шаге: файл уедет на сервер
            // только на проверке перед запуском, там же посчитаем получателей.
            this.form.audienceType = 'import'
            this.form.audienceFile = file
            this.importSummary = null
            this.invalidateAudience()
        },
        selectAudience(type: AudienceType) {
            const activation = type === 'lost' ? this.lostActivation : this.hotActivation
            if (this.form.audienceType !== type || this.form.templateKey !== activation?.templateKey) {
                this.invalidateAudience()
            }
            this.form.audienceType = type
            this.form.templateKey = activation?.templateKey || null
            this.error = activation
                ? ''
                : `Эталонная активация «${
                      type === 'lost' ? 'Потерянные клиенты' : 'Горячие клиенты'
                  }» пока не настроена.`
        },
        onAudienceRangeChange() {
            if (this.form.audienceType !== 'lost') {
                this.selectAudience('lost')
                return
            }
            this.invalidateAudience()
        },
        materialMeta(material: CallbotMaterial): string {
            return `${formatMaterialDuration(material.durationSeconds)} · ${material.fileName}`
        },
        async togglePreview(material: CallbotMaterial) {
            if (this.playingId === material.id) {
                this.stopPreview()
                return
            }
            this.stopPreview()
            this.playingId = material.id
            let url = this.previewUrls[material.id]
            if (!url) {
                const blob = await getCallbotMaterialAudio(this.projectId, material.id)
                if (this.playingId !== material.id) return
                if (!blob) {
                    this.playingId = ''
                    return
                }
                url = URL.createObjectURL(blob)
                this.$set(this.previewUrls, material.id, url)
            }
            const player = new Audio(url)
            player.onended = () => this.stopPreview()
            this.previewPlayer = player
            player.play().catch(() => this.stopPreview())
        },
        stopPreview() {
            if (this.previewPlayer) {
                this.previewPlayer.pause()
                this.previewPlayer.onended = null
            }
            this.previewPlayer = null
            this.playingId = ''
        },
        cancelReview() {
            this.reviewGeneration += 1
            this.reviewLoading = false
            this.reviewStatus = ''
        },
        // У импорта нет активации и асинхронного расчёта: файл уходит на
        // сервер здесь же, а количество получателей возвращает финализация.
        async prepareImportReview(force = false) {
            const file = this.form.audienceFile
            if (!file) return
            if (!force && this.technicalRun?.calculation.status === 'ready') return
            const generation = ++this.reviewGeneration
            this.reviewLoading = true
            this.error = ''
            this.reviewStatus = 'Загружаем список и проверяем номера…'
            try {
                if (!this.technicalRun) {
                    const created = await prepareCallbotImportRun({
                        projectId: this.projectId,
                        name: this.campaignName,
                        fileName: file.name,
                    })
                    if (generation !== this.reviewGeneration) return
                    if (created.error || !created.data) {
                        throw new Error('Не удалось создать черновик рассылки')
                    }
                    this.technicalRun = created.data
                }
                const uploaded = await uploadCallbotAudience(
                    this.projectId,
                    this.technicalRun.runId,
                    file
                )
                if (generation !== this.reviewGeneration) return
                if (uploaded.error || !uploaded.data) {
                    throw new Error(
                        'Не удалось загрузить список. Проверьте формат файла.'
                    )
                }
                this.importSummary = uploaded.data
                this.technicalRun = uploaded.data.run
                if (this.technicalRun.calculation.status !== 'ready') {
                    throw new Error(
                        'В файле не нашлось номеров, по которым можно звонить.'
                    )
                }
            } catch (error) {
                if (generation !== this.reviewGeneration) return
                this.error =
                    error instanceof Error ? error.message : 'Не удалось загрузить список'
            } finally {
                if (generation === this.reviewGeneration) this.reviewLoading = false
            }
        },
        async prepareReview(force = false) {
            if (this.form.audienceType === 'import') return this.prepareImportReview(force)
            const activation = this.selectedActivation
            if (!activation) return
            if (force) this.keys.recalculate = createCallbotCommandKey()
            const generation = ++this.reviewGeneration
            this.reviewLoading = true
            this.error = ''
            this.reviewStatus = 'Импортируем активацию и проверяем аудиторию…'
            try {
                if (activation.sourceActivationId == null) return
                if (!force && this.technicalRun?.calculation.status === 'ready') return
                if (!this.technicalRun) {
                    const result = await prepareCallbotRun(
                        {
                            projectId: this.projectId,
                            name: this.campaignName,
                            sourceActivationId: activation.sourceActivationId,
                            audience:
                                this.form.audienceType === 'lost'
                                    ? {
                                          type: 'lost',
                                          lastVisitFrom: Number(this.form.lostFrom),
                                          lastVisitTo: Number(this.form.lostTo),
                                      }
                                    : { type: 'hot' },
                        },
                        this.keys.prepare
                    )
                    if (generation !== this.reviewGeneration) return
                    if (result.error || !result.data) {
                        throw new Error(result.error?.message || 'Не удалось подготовить аудиорассылку')
                    }
                    this.technicalRun = result.data
                } else if (force) {
                    const result = await recalculateCallbotRun(
                        this.projectId,
                        this.technicalRun.runId,
                        this.keys.recalculate
                    )
                    if (generation !== this.reviewGeneration) return
                    if (result.error || !result.data) {
                        throw new Error(result.error?.message || 'Не удалось повторить расчёт аудитории')
                    }
                    this.technicalRun = result.data
                }
                if (!this.technicalRun) return
                const calculated = await this.waitForCalculation(this.technicalRun, generation)
                if (generation !== this.reviewGeneration || !calculated) return
                this.technicalRun = calculated
                if (!calculated.calculation.eligible) {
                    throw new Error(
                        calculated.calculation.matched === 0
                            ? 'По выбранным условиям в аудитории нет клиентов'
                            : 'После обязательных исключений в аудитории не осталось клиентов'
                    )
                }
            } catch (error) {
                if (generation === this.reviewGeneration) {
                    this.error = error instanceof Error ? error.message : 'Не удалось рассчитать аудиторию'
                }
            } finally {
                if (generation === this.reviewGeneration) {
                    this.reviewLoading = false
                    this.reviewStatus = ''
                }
            }
        },
        async waitForCalculation(initialRun: CallbotRun, generation: number): Promise<CallbotRun | null> {
            let run = initialRun
            for (let attempt = 0; attempt < 60; attempt += 1) {
                if (generation !== this.reviewGeneration) return null
                if (run.calculation.status === 'ready') return run
                if (run.calculation.status === 'error') {
                    throw new Error(
                        `Не удалось рассчитать аудиторию: ${run.calculation.errorCode || 'ошибка'}`
                    )
                }
                await new Promise(resolve => window.setTimeout(resolve, 1000))
                if (generation !== this.reviewGeneration) return null
                const result = await getCallbotRunStatus(this.projectId, run.runId)
                if (result.error || !result.data) {
                    throw new Error(
                        result.error?.message || 'Не удалось получить результат расчёта аудитории'
                    )
                }
                run = result.data
            }
            throw new Error('Расчёт аудитории занял слишком много времени. Попробуйте ещё раз.')
        },
        async launch() {
            // У импортированной аудитории активации нет вовсе.
            const audienceReady =
                this.form.audienceType === 'import'
                    ? Boolean(this.form.audienceFile)
                    : Boolean(this.selectedActivation?.available)
            if (
                !audienceReady ||
                !this.selectedMaterial ||
                !this.technicalRun ||
                !this.selectedChannel
            ) {
                return
            }
            this.busy = true
            this.error = ''
            try {
                if (this.technicalRun.calculation.status !== 'ready') {
                    throw new Error('Сначала дождитесь проверки аудитории')
                }
                this.launchStatus = 'Подключаем аудиоролик…'
                const audio = await attachCallbotMaterial(
                    this.projectId,
                    this.technicalRun.runId,
                    this.selectedMaterial.id,
                    this.keys.audio
                )
                if (audio.error || !audio.data) {
                    throw new Error(audio.error?.message || 'Не удалось подключить аудиоролик')
                }

                this.launchStatus = 'Планируем запуск аудиорассылки…'
                const result = await startCallbotRun(
                    {
                        projectId: this.projectId,
                        runId: this.technicalRun.runId,
                        timezone: this.form.timezone,
                        slots: this.form.scheduleOption === 'custom' ? [] : [this.form.scheduleOption],
                        customWindow:
                            this.form.scheduleOption === 'custom'
                                ? { from: this.form.customFrom, to: this.form.customTo }
                                : undefined,
                        startAt: new Date(this.form.startAt).toISOString(),
                        maxAttempts: this.form.maxAttempts,
                        retryIntervalMinutes: this.form.retryIntervalMinutes,
                        transferPhone: this.form.transferPhone,
                        notification: {
                            connectorId: this.selectedChannel.connectorId,
                            channelType: this.selectedChannel.type,
                        },
                        moduleA: {
                            enabled: this.form.moduleAEnabled,
                            reminderMinutes: this.form.moduleAReminderMinutes,
                        },
                    },
                    this.keys.start
                )
                if (result.error || !result.data) {
                    throw new Error(result.error?.message || 'Не удалось запустить аудиорассылку')
                }
                this.launched = true
                this.$emit('created', result.data)
                this.$emit('input', false)
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Не удалось запустить аудиорассылку'
            } finally {
                this.busy = false
                this.launchStatus = ''
            }
        },
        close() {
            if (this.busy) return
            this.autosaveDraft()
            this.cancelReview()
            this.stopPreview()
            this.stopPaymentWatch()
            this.$emit('input', false)
        },
        formatNumber(value: number) {
            return new Intl.NumberFormat('ru-RU').format(value || 0)
        },
        formatFileSize(value: number) {
            if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} КБ`
            return `${(value / 1024 / 1024).toFixed(1)} МБ`
        },
    },
})
